import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useTheme } from '../context/ThemeContext';
import { CheckCircle2, Shield, BarChart3, Smartphone, ArrowRight, QrCode, Users, Clock, Moon, Sun, Menu, X } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm z-50 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">QRites</span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition">Features</a>
            <a href="#pricing" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition">Pricing</a>
            <a href="#contact" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition">Contact</a>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-gray-600 dark:text-gray-300"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/login')}>Sign In</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate('/register')}>
              Get Started
            </Button>
          </nav>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-gray-600 dark:text-gray-300"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Menu Panel */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-6 space-y-1">
            <a
              href="#features"
              onClick={closeMenu}
              className="block text-center py-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition"
            >
              Features
            </a>
            <a
              href="#pricing"
              onClick={closeMenu}
              className="block text-center py-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition"
            >
              Pricing
            </a>
            <a
              href="#contact"
              onClick={closeMenu}
              className="block text-center py-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition"
            >
              Contact
            </a>
            <button
              type="button"
              onClick={() => {
                closeMenu();
                navigate('/login');
              }}
              className="block w-full text-center py-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition"
            >
              Sign In
            </button>

            <div className="pt-3 space-y-3">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  closeMenu();
                  navigate('/register');
                }}
              >
                Sign Up FREE
              </Button>
              <Button
                variant="outline"
                className="w-full border-gray-900 dark:border-gray-100"
                onClick={() => {
                  closeMenu();
                  navigate('/demo');
                }}
              >
                Book a Demo
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={closeMenu}
        />
      )}

      {/* Hero Section */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-8 max-w-4xl mx-auto">
            <div className="inline-block">
              <span className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-full text-blue-600 dark:text-blue-400 text-sm font-medium">
                Enterprise Event Management Platform
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
              Professional Event Management
              <br />
              <span className="text-blue-600">Simplified</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              Streamline your corporate events with QR-based digital invitations, real-time attendance tracking, 
              and comprehensive analytics—all in one professional platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg" 
                onClick={() => navigate('/register')}
              >
                Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                className="px-8 py-6 text-lg"
                onClick={() => navigate('/demo')}
              >
                Schedule Demo
              </Button>
            </div>
            <div className="flex items-center justify-center gap-8 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>20 free invitations</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10,000+', label: 'Events Managed' },
              { value: '500K+', label: 'Total Attendees' },
              { value: '99.9%', label: 'Uptime' },
              { value: '4.9/5', label: 'Customer Rating' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need for Professional Events
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">Comprehensive tools for efficient event management</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Shield, title: 'Secure Access', desc: 'QR-based authentication ensures only authorized attendees gain entry', color: 'text-blue-600' },
              { icon: Clock, title: 'Real-time Tracking', desc: 'Monitor attendance live with instant updates and notifications', color: 'text-green-600' },
              { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Comprehensive insights for data-driven event planning', color: 'text-purple-600' },
              { icon: Smartphone, title: 'Browser-Based', desc: 'No app installation required—works on any device', color: 'text-orange-600' },
            ].map((feature, i) => (
              <Card key={i} className="p-6 hover:shadow-lg transition-smooth">
                <feature.icon className={`w-12 h-12 ${feature.color} mb-4`} />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg">Pay per invitation. No subscriptions. No hidden fees.</p>
          </div>
          
          <Card className="max-w-2xl mx-auto p-8">
            <div className="text-center space-y-6">
              <div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">Starting from</p>
                <p className="text-6xl font-bold text-gray-900 dark:text-white">
                  ₹11.99
                </p>
                <p className="text-gray-600 dark:text-gray-400 mt-2">per invitation</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-green-700 dark:text-green-400 font-medium">New users receive 20 free guest registrations</p>
              </div>
              <div className="space-y-3 text-left">
                {[
                  'Unlimited events and sessions',
                  'Real-time attendance tracking',
                  'Advanced analytics dashboard',
                  'Multi-entry session control',
                  'QR code generation & scanning',
                  'Priority email support',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg" 
                onClick={() => navigate('/register')}
              >
                Start Free Trial
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
            Ready to Streamline Your Events?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">Join leading organizations using QRites</p>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-6 text-lg" 
            onClick={() => navigate('/register')}
          >
            Get Started Now <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">QRites</span>
          </div>
          <p className="text-gray-600 dark:text-gray-400">&copy; 2026 QRites. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;